import * as React from 'react';
import {
  FluentProvider,
  makeStyles,
  tokens,
  Theme,
} from '@fluentui/react-components';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { LoginView } from './auth/LoginView';
import {
  WorkspaceResolverProvider,
  useWorkspaceResolver,
} from './sync/WorkspaceResolverContext';
import { PresentationSyncProvider } from './sync/PresentationSyncContext';
import { AmbiguousWorkspaceView } from './views/AmbiguousWorkspaceView';
import { HomeView } from './views/HomeView';
import { WorkspaceStatusView } from './views/WorkspaceStatusView';
import { AddInIntlProvider } from './intl/AddInIntlProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  getInitialTaskpaneTheme,
  showellDarkTheme,
  showellLightTheme,
} from './theme';

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

function AppContent() {
  const { isAuthenticated } = useAuth();
  const { status, errorMessage, retry, matchingWorkspaces } =
    useWorkspaceResolver();

  if (!isAuthenticated) {
    return <LoginView />;
  }

  // Show a spinner while the resolver is figuring out which workspace owns
  // the open document — otherwise HomeView flashes briefly before the real
  // state (ambiguous picker, multiple-in-workspace, etc.) renders.
  if (
    status === 'idle' ||
    status === 'resolving' ||
    status === 'no-document' ||
    status === 'error'
  ) {
    return (
      <WorkspaceStatusView
        status={status}
        errorMessage={errorMessage}
        retry={retry}
      />
    );
  }

  if (status === 'ambiguous' && matchingWorkspaces) {
    return <AmbiguousWorkspaceView matchingWorkspaces={matchingWorkspaces} />;
  }

  return (
    <PresentationSyncProvider>
      <HomeView />
    </PresentationSyncProvider>
  );
}

export function App() {
  const styles = useStyles();
  const [theme, setTheme] = React.useState<Theme>(() =>
    getInitialTaskpaneTheme()
  );
  React.useEffect(() => {
    const syncTheme = () => {
      const isDarkTheme = Boolean(Office.context.officeTheme?.isDarkTheme);
      setTheme(isDarkTheme ? showellDarkTheme : showellLightTheme);
    };

    syncTheme();
  }, []);

  return (
    <FluentProvider
      theme={theme}
      className={styles.root}
      style={{ height: '100vh' }}
    >
      <AddInIntlProvider>
        <ErrorBoundary>
          <AuthProvider>
            <WorkspaceResolverProvider>
              <AppContent />
            </WorkspaceResolverProvider>
          </AuthProvider>
        </ErrorBoundary>
      </AddInIntlProvider>
    </FluentProvider>
  );
}
